/**
 * Phase 3b §3 — Employee portal at `/portal/employee/:employeeId`.
 *
 * Five phases driven by `GET /api/portal/employee/{employee_id}/home`:
 *   1. invited_not_started        — rich editorial hero (§3.1)
 *   2. application_in_progress    — auto-redirect to /apply/form?invite=…
 *   3. submitted_pending_uw       — compact status card (§3.3)
 *   4. policy_issued              — placeholder (§3.4)
 *   5. departed                   — placeholder (§3.5)
 *
 * All phases render inside `<PortalChrome actorType="employee">` for the
 * shared header / footer (same chrome Phase 3a established).
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import PortalChrome from "../components/portal/PortalChrome";
import CharlesStory from "../components/employee/CharlesStory";
import ChronicCareV2 from "../components/employee/ChronicCareV2";
import { track } from "../lib/analytics";
import { CALENDLY_SUCCESS_TEAM_URL } from "../config/constants";
import {
    API,
    employeePortalHome,
    employeePortalLogout,
    employeePortalRateProbe,
} from "../lib/api";
import "./PortalEmployee.css";
import "../components/portal/PortalChrome.css";

/* ------------------------------------------------------------------ helpers */
function fmtUSDCents(cents, { decimals = 2 } = {}) {
    if (cents == null) return "—";
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(cents / 100);
}

function payrollFrequencyLabel(freq) {
    switch ((freq || "").toLowerCase()) {
        case "weekly": return "a paycheck";
        case "biweekly": return "a paycheck";
        case "semimonthly": return "a paycheck";
        case "monthly": return "a month";
        default: return "a paycheck";
    }
}

/* ------------------------------------------------------------- 3.1 Phase 1 */
function PhaseInvited({ home, t, firstName, onStartApp, onComeBackLater }) {
    const ctx = home.context || {};
    const employerName = ctx.employer_name || "your employer";
    const age = ctx.age_at_lock;
    const defaultCents = ctx.default_coverage_cents;
    const premiumCents = ctx.premium_biweekly_cents_for_default;
    const defaultDollars = defaultCents ? Math.round(defaultCents / 100) : 50_000;

    const [coverageDollars, setCoverageDollars] = useState(defaultDollars);
    const [premiumLive, setPremiumLive] = useState(premiumCents);
    const [probing, setProbing] = useState(false);

    // Debounced rate probe as the slider moves.
    useEffect(() => {
        if (coverageDollars === defaultDollars && premiumCents) {
            setPremiumLive(premiumCents);
            return;
        }
        const tid = setTimeout(async () => {
            setProbing(true);
            try {
                const res = await employeePortalRateProbe(home.employee_id, coverageDollars * 100);
                setPremiumLive(res.premium_biweekly_cents);
            } catch (_) {
                // keep last good value
            } finally {
                setProbing(false);
            }
        }, 180);
        return () => clearTimeout(tid);
    }, [coverageDollars, defaultDollars, home.employee_id, premiumCents]);

    const isMonthly = (ctx.payroll_frequency || "").toLowerCase() === "monthly";
    const perLabel = isMonthly
        ? t("portal.employee.rate.perMonth", { premium: premiumLive != null ? fmtUSDCents(premiumLive) : "—" })
        : t("portal.employee.rate.perPaycheck", { premium: premiumLive != null ? fmtUSDCents(premiumLive) : "—" });

    return (
        <div className="pe-phase-invited" data-testid="pe-phase-invited">
            {/* Section A — Hero */}
            <header className="pe-hero">
                <h1 className="pe-h1" data-testid="pe-hero-h1">
                    {firstName
                        ? t("portal.employee.heroTitleNamed", { firstName, employerName })
                        : t("portal.employee.heroTitleUnnamed", { employerName })}
                </h1>
                <p className="pe-hero-sub">
                    {t("portal.employee.heroSub", { employerName })}
                </p>
            </header>

            {/* Section B — Your rate */}
            <section className="pe-rate-card" data-testid="pe-rate-card">
                <div className="pe-rate-head">
                    <div className="pe-rate-label mono">{t("portal.employee.rate.label")}</div>
                    <div className="pe-rate-meta mono">
                        {t("portal.employee.rate.metaAgeLocked", { age: age ?? "—" })}
                    </div>
                </div>
                <div className="pe-rate-numbers">
                    <div className="pe-rate-premium mono" data-testid="pe-rate-premium">
                        {premiumLive != null ? fmtUSDCents(premiumLive) : t("portal.employee.rate.dash")}
                    </div>
                    <div className="pe-rate-per">{perLabel}</div>
                </div>
                <div className="pe-coverage-slider">
                    <label htmlFor="pe-cov" className="mono pe-slider-label">
                        {t("portal.employee.rate.coverage")}
                        <strong data-testid="pe-coverage-value">${coverageDollars.toLocaleString()}</strong>
                        {probing && <span className="pe-slider-probing mono">{t("portal.employee.rate.coverageUpdating")}</span>}
                    </label>
                    <input
                        id="pe-cov"
                        type="range"
                        min={10_000}
                        max={250_000}
                        step={10_000}
                        value={coverageDollars}
                        onChange={(e) => setCoverageDollars(Number(e.target.value))}
                        data-testid="pe-coverage-slider"
                    />
                    <div className="pe-slider-ticks mono" aria-hidden>
                        <span>{t("portal.employee.rate.tickMin")}</span>
                        <span>{t("portal.employee.rate.tickMax")}</span>
                    </div>
                </div>
            </section>

            {/* Section C — Three ownership benefits */}
            <section className="pe-benefits" data-testid="pe-benefits">
                <h2 className="pe-h2">{t("portal.employee.benefits.h2")}</h2>
                <div className="pe-benefits-grid">
                    <article className="pe-benefit" data-testid="pe-benefit-rate-locked">
                        <div className="pe-benefit-num mono">01</div>
                        <h3>{t("portal.employee.benefits.rateLocked.title")}</h3>
                        <p>{t("portal.employee.benefits.rateLocked.body", { age: age ?? "32" })}</p>
                    </article>
                    <article className="pe-benefit" data-testid="pe-benefit-portable">
                        <div className="pe-benefit-num mono">02</div>
                        <h3>{t("portal.employee.benefits.portable.title")}</h3>
                        <p>{t("portal.employee.benefits.portable.body", { employerName })}</p>
                    </article>
                    <article className="pe-benefit" data-testid="pe-benefit-cash-value">
                        <div className="pe-benefit-num mono">03</div>
                        <h3>{t("portal.employee.benefits.cashValue.title")}</h3>
                        <p>{t("portal.employee.benefits.cashValue.body")}</p>
                    </article>
                </div>
            </section>

            {/* Section D — Charles's story (shared component, verbatim copy) */}
            <section className="pe-charles-section">
                <CharlesStory firstName={firstName} />
            </section>

            {/* Section E — Chronic care reveal (Version B, verbatim) */}
            <ChronicCareV2 />

            {/* Section F — Your move */}
            <section className="pe-cta" data-testid="pe-cta">
                <h2 className="pe-h2">{t("portal.employee.ctas.h2")}</h2>
                <div className="pe-cta-actions">
                    <button
                        type="button"
                        className="pe-btn pe-btn-primary"
                        onClick={onStartApp}
                        data-testid="pe-cta-start"
                    >
                        {t("portal.employee.ctas.start")}
                    </button>
                    <button
                        type="button"
                        className="pe-btn pe-btn-secondary"
                        onClick={onComeBackLater}
                        data-testid="pe-cta-later"
                    >
                        {t("portal.employee.ctas.later")}
                    </button>
                    <a
                        href={CALENDLY_SUCCESS_TEAM_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pe-link"
                        data-testid="pe-cta-ask"
                    >
                        {t("portal.employee.ctas.ask")}
                    </a>
                </div>
            </section>
        </div>
    );
}

/* ---------------------------------------------------------- 3.2 in-progress */
function PhaseInProgress({ home, onResume, t }) {
    useEffect(() => {
        const tid = setTimeout(onResume, 700);
        return () => clearTimeout(tid);
    }, [onResume]);

    return (
        <div className="pe-phase-redirect" data-testid="pe-phase-in-progress">
            <div className="mono pe-small-label">{t("portal.employee.phaseInProgress.label")}</div>
            <h1 className="pe-h1">{t("portal.employee.phaseInProgress.h1")}</h1>
            <p className="pe-hero-sub">{t("portal.employee.phaseInProgress.sub")}</p>
            <button
                type="button"
                className="pe-btn pe-btn-primary"
                onClick={onResume}
                data-testid="pe-resume-now"
            >
                {t("portal.employee.phaseInProgress.resumeNow")}
            </button>
        </div>
    );
}

/* --------------------------------------------------------- 3.3 submitted UW */
function PhaseSubmitted({ home, t }) {
    const ctx = home.context || {};
    const submitted = ctx.submitted_at
        ? new Date(ctx.submitted_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
        : "—";
    const days = ctx.expected_decision_days || 14;

    return (
        <div className="pe-phase-simple" data-testid="pe-phase-submitted">
            <div className="mono pe-eyebrow">{t("portal.employee.phaseSubmitted.eyebrow")}</div>
            <h1 className="pe-h1">{t("portal.employee.phaseSubmitted.h1")}</h1>
            <p className="pe-hero-sub">{t("portal.employee.phaseSubmitted.sub", { days })}</p>
            <dl className="pe-status-dl">
                <div>
                    <dt className="mono">{t("portal.employee.phaseSubmitted.submittedLabel")}</dt>
                    <dd>{submitted}</dd>
                </div>
                <div>
                    <dt className="mono">{t("portal.employee.phaseSubmitted.expectedLabel")}</dt>
                    <dd>{t("portal.employee.phaseSubmitted.expectedValue", { days })}</dd>
                </div>
            </dl>
            <div className="pe-cta-actions">
                <span
                    className="pe-link pe-link-disabled"
                    aria-disabled="true"
                    role="link"
                    tabIndex={-1}
                    data-testid="pe-cta-view-submitted"
                >
                    {t("portal.employee.phaseSubmitted.viewSubmitted")}
                </span>
                <span className="pe-cta-caption mono">
                    {t("portal.employee.phaseSubmitted.viewSubmittedCaption")}
                </span>
            </div>
        </div>
    );
}

/* --------------------------------------------------------- 3.4 policy issued */
function PhasePolicyIssued({ t }) {
    return (
        <div className="pe-phase-simple" data-testid="pe-phase-policy-issued">
            <div className="mono pe-eyebrow">{t("portal.employee.phasePolicyIssued.eyebrow")}</div>
            <h1 className="pe-h1">{t("portal.employee.phasePolicyIssued.h1")}</h1>
            <p className="pe-hero-sub">{t("portal.employee.phasePolicyIssued.sub")}</p>
            <a
                href={CALENDLY_SUCCESS_TEAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="pe-btn pe-btn-primary"
                data-testid="pe-policy-contact"
            >
                {t("portal.employee.phasePolicyIssued.cta")}
            </a>
        </div>
    );
}

/* -------------------------------------------------------------- 3.5 departed */
function PhaseDeparted({ t }) {
    return (
        <div className="pe-phase-simple" data-testid="pe-phase-departed">
            <div className="mono pe-eyebrow">{t("portal.employee.phaseDeparted.eyebrow")}</div>
            <h1 className="pe-h1">{t("portal.employee.phaseDeparted.h1")}</h1>
            <a
                href={CALENDLY_SUCCESS_TEAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="pe-btn pe-btn-primary"
                data-testid="pe-departed-contact"
            >
                {t("portal.employee.phaseDeparted.cta")}
            </a>
        </div>
    );
}


/* ============================================================= Main export */
export default function PortalEmployee() {
    const { employeeId } = useParams();
    const { t } = useTranslation();
    const [home, setHome] = useState(null);
    const [err, setErr] = useState(null);
    const navigate = useNavigate();

    // Phase 3b §1.1 — If the URL carries `?invite=<tok>`, route through the
    // backend auto-auth endpoint. It mints/returns the portal cookie + 302s
    // back to a clean /portal/employee/{id}. We detect the param first
    // because the home() call would 401 without a cookie.
    useEffect(() => {
        const qs = new URLSearchParams(window.location.search);
        const invite = qs.get("invite");
        if (invite) {
            window.location.replace(`${API}/employee/invite-auth?token=${encodeURIComponent(invite)}`);
        }
    }, []);

    const load = useCallback(async () => {
        // Skip the home call if we're about to redirect for auto-auth.
        const qs = new URLSearchParams(window.location.search);
        if (qs.get("invite")) return;
        try {
            const h = await employeePortalHome(employeeId);
            setHome(h);
        } catch (e) {
            if (e?.response?.status === 401) {
                navigate("/apply", { replace: true });
                return;
            }
            if (e?.response?.status === 403) {
                setErr(t("portal.employee.errors.wrongSession"));
                return;
            }
            setErr(e?.response?.data?.detail || t("portal.employee.errors.generic"));
        }
    }, [employeeId, navigate, t]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!home) return;
        track("portal.home_viewed", { phase: home.phase, actor: "employee" });
        document.title = "Your Laguna portal";
    }, [home]);

    const signOut = useCallback(async () => {
        await employeePortalLogout();
        window.location.href = "/";
    }, []);

    const comeBackLater = useCallback(async () => {
        track("employee_portal.come_back_later", {});
        await employeePortalLogout();
        // Use the i18n'd confirmation; alert is acceptable UX for the
        // logout-and-go flow (it's a terminal action on this page).
        alert(t("portal.employee.ctas.laterSaved"));
        window.location.href = "/";
    }, [t]);

    const startApp = useCallback(() => {
        track("portal.application_started", { employee_id: employeeId });
        const token = home?.context?.invite_token;
        const url = token ? `/apply/form?invite=${encodeURIComponent(token)}` : "/apply/form";
        window.location.href = url;
    }, [employeeId, home]);

    if (err) {
        return (
            <PortalChrome actorType="employee">
                <div className="pe-wrap" data-testid="pe-error">
                    <h1 className="pe-h1">{err}</h1>
                    <button className="pe-btn pe-btn-secondary" onClick={signOut} data-testid="pe-error-signout">
                        {t("portal.employee.errors.signOut")}
                    </button>
                </div>
            </PortalChrome>
        );
    }

    if (!home) {
        return (
            <PortalChrome actorType="employee">
                <div className="pe-wrap pe-centered" data-testid="pe-loading">
                    <div className="mono pe-small-label">{t("portal.employee.loading")}</div>
                </div>
            </PortalChrome>
        );
    }

    const firstName = home.context?.first_name || "";
    let body;
    switch (home.phase) {
        case "invited_not_started":
            body = <PhaseInvited home={home} t={t} firstName={firstName} onStartApp={startApp} onComeBackLater={comeBackLater} />;
            break;
        case "application_in_progress":
            body = <PhaseInProgress home={home} onResume={startApp} t={t} />;
            break;
        case "submitted_pending_uw":
            body = <PhaseSubmitted home={home} t={t} />;
            break;
        case "policy_issued":
            body = <PhasePolicyIssued t={t} />;
            break;
        case "departed":
            body = <PhaseDeparted t={t} />;
            break;
        default:
            body = <PhaseInvited home={home} t={t} firstName={firstName} onStartApp={startApp} onComeBackLater={comeBackLater} />;
    }

    return (
        <PortalChrome
            actorType="employee"
            displayName={firstName || home.context?.employer_name || "Your portal"}
            onSignOut={signOut}
        >
            <div className="pe-wrap" data-phase={home.phase} data-testid="pe-wrap">
                {body}
            </div>
        </PortalChrome>
    );
}
